// AFTER
import { SEOHead } from "@components/organisms";
import { InnerPageLayout } from "@components/templates";
import { Button, InputField } from "@components/atoms";
import { SuccessModal } from "@components/molecules";
import { useAuth } from "@app/providers/useAuth";
import { realAuthService } from "@modules/auth/services/auth.service";
import { User, Camera } from "lucide-react";
import { onFirebaseAuthChanged, getFirebaseCurrentUser } from "@services/config/firebase";
import { useState, useEffect } from "react";

export function ProfilePage() {
  const { user, refreshUser } = useAuth();

  // Read directly from the Firebase User object, same pattern as the reference app.
  // auth.currentUser is the live Firebase User — has displayName, email, photoURL.
  const [firebaseUser, setFirebaseUser] = useState(getFirebaseCurrentUser);

  useEffect(() => {
    const unsubscribe = onFirebaseAuthChanged((fbUser) => {
      setFirebaseUser(fbUser);
    });
    return unsubscribe; // cleans up listener on unmount
  }, []);

  const [isEditing, setIsEditing] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Form state — prioriza dato del backend (user) sobre Firebase displayName.
  // Firebase displayName no se actualiza cuando el usuario edita su perfil en el backend.
  const [fullName, setFullName] = useState(user?.name ?? firebaseUser?.displayName ?? "");
  const [cedula, setCedula] = useState(user?.cedula ?? "");
  const [email, setEmail] = useState(user?.mail ?? firebaseUser?.email ?? "");
  const [phone, setPhone] = useState(user?.phone ?? "");
  const [address, setAddress] = useState(user?.address ?? "");

  const handleSave = async () => {
    setIsLoading(true);
    setErrorMessage("");

    try {
      const response = await realAuthService.updateProfile(
        email,
        fullName,
        address,
        phone,
        cedula || undefined,
      );

      if (response.success) {
        // Recargar user desde el backend para sincronizar el AuthContext.
        // Sin esto, la próxima visita al perfil muestra el dato viejo de Firebase.
        await refreshUser();
        setIsEditing(false);
        setShowSuccess(true);
      } else {
        setErrorMessage(
          response.message || "Error al guardar los cambios. Intenta de nuevo.",
        );
      }
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : "Error al guardar los cambios",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const inputVariant = isEditing ? "editable" : "disabled-view";

  return (
    <>
      <SEOHead title="Mi perfil" noIndex />
      <InnerPageLayout title="Mi perfil">
        <div className="flex flex-col items-start px-4 sm:pl-8">
          {/* Avatar */}
          <div className="relative mb-6">
            <div className="flex h-[105px] w-[105px] items-center justify-center rounded-full bg-neutral-70 overflow-hidden">
              {firebaseUser?.photoURL ? (
                <img
                  src={firebaseUser.photoURL}
                  alt={firebaseUser.displayName ?? "Avatar"}
                  className="h-full w-full object-cover"
                  referrerPolicy="no-referrer"  // ← Google Photos blocks requests without this
                  onError={(e) => {
                    // If image fails to load, fall back to the icon
                    e.currentTarget.style.display = "none";
                    e.currentTarget.nextElementSibling?.removeAttribute("hidden");
                  }}
                />
              ) : <User
                size={70}
                className="text-neutral-10"
              />}
              {/* Fallback icon — shown when no photoURL or image fails to load */}
              
            </div>
            {/* Camera overlay */}
            <div className="absolute bottom-0 right-0 flex h-[35px] w-[35px] items-center justify-center rounded-full bg-neutral-80">
              <Camera size={17} className="text-white" />
            </div>
          </div>

          {/* Identity header — sourced directly from Firebase, not editable */}
          <div className="mb-4">
            <p className="text-lg font-semibold text-black">
              {firebaseUser?.displayName || user?.name || "Usuario"}
            </p>
            <p className="text-sm text-neutral-40">
              {firebaseUser?.email || user?.mail || "usuario@mail.com"}
            </p>
          </div>

          {/* Form fields */}
          <div className="flex w-full max-w-[665px] flex-col gap-4">
            <InputField
              label="Nombres y Apellidos"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              variant={inputVariant}
            />
            <InputField
              label="Cédula"
              value={cedula}
              onChange={(e) => setCedula(e.target.value)}
              variant={inputVariant}
            />
            <InputField
              label="Correo Electrónico"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              variant={inputVariant}
              type="email"
            />
            <InputField
              label="Celular"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              variant={inputVariant}
            />
            <InputField
              label="Dirección"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              variant={inputVariant}
            />
          </div>

          {/* Edit / Save button */}
          <div className="mt-6 flex w-full max-w-[665px] mb-4">
            {isEditing ? (
              <>
                <Button
                  className="w-full max-w-[340px] h-[50px] text-h5 bg-primary-110 text-white hover:bg-primary-110 hover:opacity-90 disabled:opacity-50"
                  onClick={handleSave}
                  disabled={isLoading}
                >
                  {isLoading ? "Guardando..." : "Guardar"}
                </Button>
              </>
            ) : (
              <Button
                className="w-full max-w-[340px] h-[50px] text-h5 bg-white border border-primary-110 text-primary-110 hover:bg-primary-10"
                onClick={() => setIsEditing(true)}
              >
                Editar
              </Button>
            )}
          </div>

          {/* Error message */}
          {errorMessage && (
            <div className="mt-4 w-full max-w-[665px] rounded-lg bg-red-100 px-4 py-3 text-red-700 text-sm">
              {errorMessage}
            </div>
          )}
        </div>
      </InnerPageLayout>

      <SuccessModal
        isOpen={showSuccess}
        onClose={() => setShowSuccess(false)}
        title="¡Cambios guardados!"
        message="Hemos guardado tus cambios correctamente"
      />
    </>
  );
}
